import CQModificationsFormProvider from "pages/CustomQuote/AddQuotePage/components/CQModifications/forms/CQModificationsFormProvider.tsx";
import {
	AddQuoteLineItemGridContainer,
	ChargesContainer,
	CQModificationContainer,
	CqModificationsContainer,
	ItemAndModsContainer,
	ManualAdjustmentsContainer,
	QuotePageFooter,
	QuotePageFooterExport,
	RangesContainer,
	RequiredFieldWrapper
} from "pages/CustomQuote/AddQuotePage/addQuotePage.styles.ts";
import { AddModificationButtonText, CQCodeLabelText } from "components/NewOrders/AddModification/constants.ts";
import { LinkButton } from "components/Common/Link";
import {
	AdditionalModNotesText,
	BaseItemListPriceText,
	CustomizationChargeText,
	ManualFlatAdjustment,
	ManualPercentAdjustment,
	RemoveText,
	RequiredFieldsText
} from "constants/text.ts";
import AutoComplete from "components/Common/Autocomplete/Autocomplete.tsx";
import { SkuAndDescription } from "components/NewOrders/AddLineItem/schema.ts";
import { convertSkuAndDescriptionToString, formatPriceWithPrefix, sortSkuAndDescription } from "utils/string.ts";
import AddIcon from "@mui/icons-material/Add";
import { Button, TextField } from "@mui/material";
import { FieldGroup } from "pages/Replacements/components/AddNewAddressModal/addNewAddressModal.styles.ts";
import { Controller, useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { MANUAL_PRICE_ADJ, MAX_NOTES_LENGTH } from "pages/CustomQuote/AddQuotePage/utils/fileUtils.ts";
import HelperText from "components/Form/HelperText/HelperText.tsx";
import { inputErrorHandling } from "pages/CustomQuote/AddQuotePage/utils/validationUtils.ts";
import { AddAndExportToAutoCADText, TotalPriceText } from "components/NewOrders/constants.ts";
import { models } from "types/api/viewModels.ts";
import { CQModificationsFormFields } from "pages/CustomQuote/AddQuotePage/components/CQModifications/forms/CQModificationsFormSchema.ts";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getCqParentLineItemBySku } from "pages/CustomQuote/AddQuotePage/AddQuotePage.tsx";
import { useChargesCalculation } from "pages/CustomQuote/AddQuotePage/hooks/useChargesCalculation.ts";
import { CQ_CODES } from "pages/CustomQuote/customQuoteModificationCodes.ts";
import { useGetModificationsQuery } from "features/api/newOrderApi.ts";
import { getComplexityTierLabel } from "pages/CustomQuote/AddQuotePage/upchargeUtils.ts";
import { AddCqModificationButton } from "pages/CustomQuote/AddQuotePage/components/CQModifications/CQModifications.styles.ts";

interface CQModificationsProps {
	revision: models["CustomQuoteRevisionViewModel"] | undefined;
	specialMods: models["SpecialModificationViewModel"][];
	getAutoCADFile: () => Promise<void>;
	baseQuoteTotal: number;
	complexityTierConfig: models["CustomQuoteConfigViewModel"] | undefined;
	onValuesChange?: (values: CQModificationsFormFields) => void;
	setTotal: (total: number) => void;
}

export const CQModificationsForm = ({
	revision,
	specialMods,
	getAutoCADFile,
	baseQuoteTotal,
	complexityTierConfig,
	onValuesChange,
	setTotal
}: CQModificationsProps) => {
	const { control, handleSubmit, watch, setValue, getValues } = useFormContext<CQModificationsFormFields>();

	const [autoFocusRangeIndex, setAutoFocusRangeIndex] = useState<number | null>(null);

	const {
		fields: cqModFields,
		append: appendCqMod,
		remove: removeCqMod
	} = useFieldArray({ control, name: "cqMods" });

	const {
		fields: specialModFields,
		append: appendSpecialMod,
		remove: removeSpecialMod
	} = useFieldArray({ control, name: "specialMods" });

	const selectedCqCode = watch("cqCode");
	const cqLineItemMap: SkuAndDescription[] | undefined = useMemo(() => {
		return CQ_CODES?.map((item) => {
			return { sku: item.sku, description: item.description };
		});
	}, []);
	const sortedCqOptions = useMemo(() => sortSkuAndDescription(cqLineItemMap), [cqLineItemMap]);
	const selectedCqParent = useMemo(() => getCqParentLineItemBySku(selectedCqCode), [selectedCqCode]);
	const { data: modifications } = useGetModificationsQuery(
		{
			productId: selectedCqParent?.id ?? "",
			productLineId: revision?.globals?.productLine?.id ?? "",
			includeCQComplexityMods: true
		},
		{ skip: !revision?.globals?.productLine?.id || !selectedCqParent?.id }
	);

	const deduplicatedModifications = useMemo(() => {
		const map = new Map<string, models["ModificationViewModel"]>();

		modifications?.forEach((mod) => {
			if (!mod?.id) return;
			const existing = map.get(mod.id);
			if (!existing || (mod.ranges?.length ?? 0) > (existing.ranges?.length ?? 0)) {
				map.set(mod.id, mod);
			}
		});

		return Array.from(map.values());
	}, [modifications]);

	const watchedCqMods = watch("cqMods");

	const filteredModifications = useMemo(() => {
		const cqModIds = new Set(watchedCqMods?.map((m) => m?.id).filter(Boolean));
		return deduplicatedModifications?.filter((mod) => mod?.id && !cqModIds.has(mod.id)) ?? [];
	}, [deduplicatedModifications, watchedCqMods]);
	const foundModRange = useCallback(
		(mod: models["CqModViewModel"] | null | undefined) =>
			deduplicatedModifications?.find((m) => m?.id === mod?.id)?.ranges,
		[deduplicatedModifications]
	);
	const getRanges = useCallback(
		(minValue = 0, maxValue = 0, increment = 1) =>
			Array.from(
				{ length: Math.floor((maxValue - minValue) / increment) + 1 },
				(_, i) => minValue + i * increment
			),
		[]
	);
	const { manualPercentAdjustmentAmount, manualFlatAdjustmentAmount, customizationChargeAmount, total } =
		useChargesCalculation({
			baseQuoteTotal,
			complexityTierConfig
		});

	useEffect(() => {
		setTotal(total);
	}, [total, setTotal]);

	const watchedSpecialMods = useWatch({ control, name: "specialMods" });
	const watchedCqCode = useWatch({ control, name: "cqCode" });
	const watchedModNotes = useWatch({ control, name: "modNotes" });
	const watchedManualPercent = useWatch({ control, name: "manualPercentAdjustment" });
	const watchedManualFlat = useWatch({ control, name: "manualFlatAdjustment" });

	useEffect(() => {
		onValuesChange?.({
			cqMods: watchedCqMods,
			specialMods: watchedSpecialMods,
			cqCode: watchedCqCode,
			modNotes: watchedModNotes,
			manualPercentAdjustment: watchedManualPercent,
			manualFlatAdjustment: watchedManualFlat
		});
	}, [
		watchedCqMods,
		watchedSpecialMods,
		watchedCqCode,
		watchedModNotes,
		watchedManualPercent,
		watchedManualFlat,
		onValuesChange
	]);

	useEffect(() => {
		if (!revision) return;

		const foundCqCode = CQ_CODES.find((code) => code.sku === revision?.cqLineItem?.sku);

		setValue("modNotes", revision.notes ?? "");
		setValue("cqCode", foundCqCode?.sku ?? "");
		setValue("cqMods", revision.cqLineItem?.cqMods ?? []);
		setValue("specialMods", revision.specialMods ?? []);
		setValue("manualPercentAdjustment", getComplexityTierLabel(revision.manualPercentageCharge) ?? "");
		setValue("manualFlatAdjustment", revision.manualFlatFeeCharge?.toString() ?? "");
	}, [revision, setValue]);
	return (
		<form onSubmit={handleSubmit(getAutoCADFile)}>
			<AddQuoteLineItemGridContainer>
				<ItemAndModsContainer>
					<RequiredFieldWrapper>{RequiredFieldsText}</RequiredFieldWrapper>
					<CqModificationsContainer>
						<h5>CQ Modifications</h5>
						<Controller
							name="cqCode"
							control={control}
							render={({ field: { onChange, value }, fieldState: { error } }) => (
								<AutoComplete
									options={sortedCqOptions ?? []}
									getOptionLabel={(option: SkuAndDescription) =>
										convertSkuAndDescriptionToString(option.sku, option.description) ?? ""
									}
									value={sortedCqOptions?.find((o) => o.sku === value) ?? null}
									onChange={(_e, selected: SkuAndDescription) => {
										onChange(selected?.sku ?? null);
										removeCqMod();
									}}
									isError={!!error}
									errorText={error?.message}
									required
									label={CQCodeLabelText}
									dataTestId="cq-code-select"
								/>
							)}
						/>
					</CqModificationsContainer>
					<CqModificationsContainer>
						{cqModFields.map((field, index) => (
							<CQModificationContainer key={field.id}>
								<Controller
									name={`cqMods.${index}`}
									control={control}
									render={({ field: { onChange, value }, fieldState: { error } }) => (
										<AutoComplete
											options={filteredModifications ?? []}
											onChange={(_e, selected) => {
												onChange({ ...selected, value: null });
												setAutoFocusRangeIndex(foundModRange(selected)?.length ? index : null);
											}}
											value={value ?? null}
											isError={!!error}
											errorText={error?.message}
											virtualize
											getOptionLabel={(option: SkuAndDescription) =>
												convertSkuAndDescriptionToString(option.sku, option.description) ?? ""
											}
											label="Select Modification Type"
											dataTestId={`cq-mod-select-${index}`}
											required
										/>
									)}
								/>
								{Boolean(foundModRange(watchedCqMods?.[index])?.length) && (
									<RangesContainer>
										{foundModRange(watchedCqMods?.[index])?.map((range, rangeIndex) => {
											const ranges = getRanges(range.minValue, range.maxValue, range.increment);
											return (
												<Controller
													key={range.description}
													name={`cqMods.${index}.value`}
													control={control}
													render={({ field: { onChange, value } }) => (
														<AutoComplete
															options={ranges}
															// eslint-disable-next-line sonarjs/no-nested-functions
															onChange={(_e, selected) => {
																onChange(selected);
																setAutoFocusRangeIndex((prev) =>
																	prev === index ? null : prev
																);
															}}
															value={value ?? null}
															errorText={undefined}
															isError={false}
															label={`Select ${range.description}`}
															dataTestId={`cq-mod-range-${index}`}
															required
															autoFocus={
																rangeIndex === 0 && autoFocusRangeIndex === index
															}
															helperText={`Min: ${range.minValue}", Max: ${range.maxValue}`}
														/>
													)}
												/>
											);
										})}
									</RangesContainer>
								)}
								<LinkButton
									variant="remove"
									onClick={() => removeCqMod(index)}
								>
									{RemoveText}
								</LinkButton>
							</CQModificationContainer>
						))}
						{selectedCqParent?.id && (
							<AddCqModificationButton
								variant="text"
								data-testid="add-cq-mod-button"
								onClick={() => appendCqMod({})}
							>
								<AddIcon />
								{AddModificationButtonText}
							</AddCqModificationButton>
						)}
					</CqModificationsContainer>
					<CqModificationsContainer>
						<h5>Special Modifications</h5>
						{specialModFields.map((field, index) => (
							<CQModificationContainer key={field.id}>
								<Controller
									name={`specialMods.${index}`}
									control={control}
									render={({ field: { onChange, value }, fieldState: { error } }) => (
										<AutoComplete
											options={specialMods ?? []}
											onChange={(_e, selected) => {
												if (selected?.notes) {
													const currentNotes = getValues("modNotes");
													setValue("modNotes", `${currentNotes} ${selected.notes}`);
												}
												onChange(selected);
											}}
											value={value ?? null}
											isError={!!error}
											errorText={error?.message}
											virtualize
											getOptionLabel={(option: SkuAndDescription) =>
												convertSkuAndDescriptionToString(option.sku, option.description) ?? ""
											}
											label="Select Special Modification Type"
											dataTestId="special-mod-select"
											required
										/>
									)}
								/>
								{Boolean(watch(`specialMods.${index}`)?.description) && (
									<RangesContainer>
										<TextField
											size="small"
											label="Modification Description"
											defaultValue={watch(`specialMods.${index}`)?.description}
										/>
									</RangesContainer>
								)}
								<LinkButton
									variant="remove"
									onClick={() => removeSpecialMod(index)}
								>
									{RemoveText}
								</LinkButton>
							</CQModificationContainer>
						))}
						<AddCqModificationButton
							variant="text"
							data-testid="add-special-mod-button"
							onClick={() => appendSpecialMod({})}
						>
							<AddIcon />
							Add Special Modification
						</AddCqModificationButton>
					</CqModificationsContainer>

					<FieldGroup>
						<Controller
							name="modNotes"
							control={control}
							render={({ field, fieldState }) => {
								const value = typeof field.value === "string" ? field.value : "";
								const isLimitError = value.length > MAX_NOTES_LENGTH;
								return (
									<TextField
										label={AdditionalModNotesText}
										multiline
										sx={{ marginTop: "16px" }}
										rows={3}
										value={value}
										onChange={field.onChange}
										error={isLimitError || !!fieldState.error}
										slotProps={{
											inputLabel: { shrink: true },
											input: { inputProps: { "data-testid": "additional-mod-notes" } }
										}}
										helperText={
											<HelperText
												isError={isLimitError || !!fieldState.error}
												text={inputErrorHandling(value, fieldState)}
												characterLimit={{
													currentLength: value.length,
													limit: MAX_NOTES_LENGTH,
													isLimitError
												}}
											/>
										}
									/>
								);
							}}
						/>
					</FieldGroup>
				</ItemAndModsContainer>

				<QuotePageFooter>
					<ManualAdjustmentsContainer>
						<Controller
							name="manualPercentAdjustment"
							control={control}
							render={({ field: { onChange, value }, fieldState: { error } }) => (
								<AutoComplete
									options={MANUAL_PRICE_ADJ}
									onChange={(_, newValue) => onChange(newValue)}
									isError={!!error}
									errorText={error?.message}
									label={ManualPercentAdjustment}
									dataTestId="add-manual-percent-adjustement"
									value={value || ""}
								/>
							)}
						/>
						<Controller
							name="manualFlatAdjustment"
							control={control}
							render={({ field: { onChange, value }, fieldState: { error } }) => (
								<TextField
									size="small"
									fullWidth
									label={ManualFlatAdjustment}
									value={value ?? ""}
									onChange={(event) => {
										const nextValue = event.target.value;
										if (/^[+-]?\d*(?:\.\d*)?$/.test(nextValue)) {
											onChange(nextValue);
										}
									}}
									error={!!error}
									helperText={error?.message}
									slotProps={{
										input: {
											inputProps: { "data-testid": "manual-flat-adjustment-input" },
											inputMode: "decimal"
										}
									}}
								/>
							)}
						/>
					</ManualAdjustmentsContainer>
					<ChargesContainer>
						<div>
							<span>{BaseItemListPriceText}</span>
							<span>{formatPriceWithPrefix(baseQuoteTotal, false, true)}</span>
						</div>
						<div>
							<span>{CustomizationChargeText}</span>
							<span>{formatPriceWithPrefix(customizationChargeAmount, false, true)}</span>
						</div>
						<div>
							<span>{ManualPercentAdjustment}</span>
							<span>{formatPriceWithPrefix(manualPercentAdjustmentAmount, false, true)}</span>
						</div>
						<div>
							<span>{ManualFlatAdjustment}</span>
							<span>{formatPriceWithPrefix(manualFlatAdjustmentAmount, false, true)}</span>
						</div>
						<div>
							<span>{TotalPriceText}</span>
							<span>{formatPriceWithPrefix(total)}</span>
						</div>
					</ChargesContainer>
				</QuotePageFooter>
				<QuotePageFooterExport>
					<Button
						type="submit"
						variant="contained"
						data-testid="add-and-export-to-autocad"
					>
						{AddAndExportToAutoCADText}
					</Button>
				</QuotePageFooterExport>
			</AddQuoteLineItemGridContainer>
		</form>
	);
};

export const CQModifications = (props: CQModificationsProps) => (
	<CQModificationsFormProvider>
		<CQModificationsForm {...props} />
	</CQModificationsFormProvider>
);
